import $ from 'jquery'
import Interact from 'interactjs'
import { Store } from 'vuex'
import RootState from '../../store/state'
import * as ComponentHelpers from '../../store/editor/components/helpers'
import { Actions, Getters } from '../../store/editor/core/helpers'
import { Direction, EAST, NORTH, SOUTH, WEST } from '../../store/editor/core/state'
import { DRAG_EVENT_CLASS_PREFIX, EventType } from './Draggable'
import ComponentBuilder from '../../store/editor/components/component-builder'
import { IComponentData } from '../../store/editor/components/state'

export { NORTH, SOUTH, EAST, WEST, Direction }
export { EventType }

export interface DropEvent {
  target: HTMLElement
  dropzone: Interact.Interactable
  relatedTarget: HTMLElement
  draggable: Interact.Interactable
  dragEvent: Interact.InteractEvent
  timestamp: number,
  type: string
}

export interface ExtendedDropEvent {
  dropping: IComponentData
  direction: Direction
  dragEventType: string
  dropEvent: DropEvent
}

export interface DropPosition {
  left: number
  top: number
  right: number
  bottom: number
  ratioX: number
  ratioY: number
}

export interface DroppableOption {
  /** ドロップ位置を判定する関数。これが指定された場合、 direction は無視する */
  detector?: (position: DropPosition) => Direction | undefined
  /** ドロップを受け付ける位置 */
  directions?: Direction[],
  /** ドラッグイベントの種類 */
  eventType?: string[]
}

const DEFAULT_OPTION: DroppableOption = {
  directions: [NORTH, SOUTH, EAST, WEST],
  eventType: [EventType.DEFAULT]
}

export default class Droppable {
  private option: DroppableOption

  constructor(
    private readonly element: HTMLElement | string,
    private readonly interactable: Interact.Interactable,
    private store: Store<RootState>,
    option?: DroppableOption) {
    this.option = Object.assign({}, DEFAULT_OPTION, option || {})
    this.init()
  }

  private get componentId(): string {
    const id = $(this.element).attr('id')
    if (!id) {
      throw new Error('コンポーネントのIDが見つかりません: ' + this.element)
    }
    return id
  }

  private get componentData(): IComponentData {
    return this.getComponentDataById(this.componentId)
  }

  private get directions(): string[] {
    return this.option.directions || []
  }

  private get eventType(): string[] {
    return this.option.eventType || [EventType.DEFAULT]
  }

  private get eventTypeAsClassList(): string {
    return this.eventType.map(type => `.${DRAG_EVENT_CLASS_PREFIX}${type}`).join(',')
  }

  public onDropTo(listener: (event: ExtendedDropEvent) => boolean | void, useCapture?: boolean): this {
    this.on('drop', (dropEvent) => {
      const dropState = Getters.dropState(this.store)
      if (!dropState.position || !dropState.eventType) {
        return
      }
      // detector によって返された方角は、（undefined を除き）どんな値でも有効とみなす
      const isDirectionAcceptable = (this.option.detector || this.directions.indexOf(dropState.position.direction) >= 0)
      if (isDirectionAcceptable && this.canMoveTo(dropEvent, dropState.position.direction)) {
        const extendedEvent: ExtendedDropEvent = {
          dropEvent,
          dropping: this.getDroppingComponent(dropEvent),
          direction: dropState.position.direction,
          dragEventType: dropState.eventType
        }
        listener(extendedEvent)
      }
    }, useCapture)
    return this
  }

  public on(eventType: string, listener: (event: DropEvent) => boolean | void, useCapture?: boolean): this {
    this.interactable.on(eventType, listener, useCapture)
    return this
  }

  public off(eventType: string, listener: (event: DropEvent) => boolean | void, useCapture?: boolean): this {
    this.interactable.off(eventType, listener, useCapture)
    return this
  }

  private getComponentDataById(id: string): IComponentData {
    return ComponentHelpers.Getters.componentFinder(this.store)(id)
  }

  private getDroppingComponent(dropEvent: DropEvent): IComponentData {
    return this.getComponentDataById(dropEvent.relatedTarget.id)
  }

  private canMoveTo(dropEvent: DropEvent, direction: Direction): boolean {
    return new ComponentBuilder(this.store)
      .canMoveTo(this.getDroppingComponent(dropEvent), {
        direction,
        destination: this.componentData
      })
  }

  private init(): void {
    this.interactable
      .dropzone({
        overlap: 'pointer',
        accept: this.eventTypeAsClassList
      })
      .on('dropmove', (event: DropEvent) => {
        const direction = this.calcDirection(event)
        if (direction && this.canMoveTo(event, direction)) {
          Actions.showDropPoint(this.store, {
            direction,
            componentId: this.componentId
          })
          return
        }
        Actions.hideDropPoint(this.store)
      })
      .on('dragleave', (event: DropEvent) => {
        Actions.hideDropPoint(this.store)
      })
      .on('dropdeactivate', (event: DropEvent) => {
        Actions.hideDropPoint(this.store)
      })
  }

  private calcDirection(event: DropEvent): Direction | undefined {
    const position = this.getDropPosition(event)
    if (this.option.detector) {
      return this.option.detector(position)
    }
    return this.getDirectionIfAcceptable(WEST, () => position.ratioX <= 0.33) ||
      this.getDirectionIfAcceptable(EAST, () => position.ratioX >= 0.67) ||
      this.getDirectionIfAcceptable(NORTH, () => position.ratioY <= 0.5) ||
      this.getDirectionIfAcceptable(SOUTH, () => position.ratioY > 0.5)
  }

  private getDropPosition(event: DropEvent): DropPosition {
    const el = $(this.element)
    const offset = el.offset() || { left: 0, top: 0 }
    const dropOffset = {
      x: event.dragEvent.pageX - offset.left,
      y: event.dragEvent.pageY - offset.top
    }
    const elementSize = {
      width: el.innerWidth() || 0,
      height: el.innerHeight() || 0
    }
    return {
      left: dropOffset.x,
      top: dropOffset.y,
      right: elementSize.width - dropOffset.x,
      bottom: elementSize.height - dropOffset.y,
      ratioX: dropOffset.x / elementSize.width,
      ratioY: dropOffset.y / elementSize.height
    }
  }

  private getDirectionIfAcceptable(direction: Direction, test: () => boolean): Direction | undefined {
    return (this.directions.indexOf(direction) >= 0 && test()) ? direction : undefined
  }
}
